import { useState } from 'react';

import { Address as AddressModel } from '@packlink/packlink-sdk';
import { Typography, Button, ButtonVariant, Dialog, DialogContent, DialogHeader } from '@shipengine/giger';
import { useTranslation } from '@packlink/translation-provider';

import { PlaceOrigin } from '@types';
import { AmplitudeEvents, useAmplitude } from '@metrics';
import { AddressForm } from '@components';
import { getAddresStyles, getListAddresStyles, getAddressErrorStyles } from './AddressStyles';

export interface IAddressProps {
    /** Specifies the title of the address */
    title: string;
    /** Contains the address details */
    initialValues?: AddressModel;
    /** Contains the address errors */
    errors?: AddressModel;
    /** Type of origin for this address. Either 'from' or 'to' */
    originType: PlaceOrigin;
    /** Callback to be executed when edit action is successful */
    onSave(address: AddressModel): void;
}

const Address = ({ title, initialValues, originType, onSave, errors }: IAddressProps): JSX.Element => {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const { t } = useTranslation();
    const sendAmplitudeEvent = useAmplitude();

    const onSaveHandler = (data: AddressModel) => {
        onSave(data);
        sendAmplitudeEvent(AmplitudeEvents.CLICK_SAVE_CHANGE_ADDRESS);
        setIsFormOpen(false);
    };

    const onCloseDialog = (): void => {
        sendAmplitudeEvent(AmplitudeEvents.CLICK_EXIT_CHANGE_ADDRESS);
        setIsFormOpen(false);
    };

    const onCancelDialog = (): void => {
        sendAmplitudeEvent(AmplitudeEvents.CLICK_CLOSE_CHANGE_ADDRESS);
        setIsFormOpen(false);
    };

    const onClickEdit = (): void => {
        originType === PlaceOrigin.FROM
            ? sendAmplitudeEvent(AmplitudeEvents.CLICK_EDIT_SEND_FROM)
            : sendAmplitudeEvent(AmplitudeEvents.CLICK_EDIT_SEND_TO);
        setIsFormOpen(true);
    };

    const textContent = {
        fullName:
            !errors?.firstName && !errors?.lastName ? `${initialValues?.firstName} ${initialValues?.lastName}` : null,
        company: initialValues?.company || errors?.company,
        street1: initialValues?.street1 || errors?.street1,
        street2: initialValues?.street2 || errors?.street2,
        city: initialValues?.city || errors?.city,
        alpha2Code: initialValues?.alpha2Code || errors?.alpha2Code,
        zipCode: initialValues?.zipCode || errors?.zipCode,
        email: initialValues?.email || errors?.email,
        phone: initialValues?.phone || errors?.phone,
    };

    const ariaLabelFor = (name: string): string => `Send ${originType.toLowerCase()} ${name}`;
    return (
        <div css={getAddresStyles}>
            <Typography variant="subtitle2" component="h5">
                {title}
            </Typography>
            <ul css={getListAddresStyles}>
                {textContent.fullName && (
                    <Typography variant="body2" component="li" aria-label={ariaLabelFor('fullname')}>
                        {textContent.fullName}
                    </Typography>
                )}
                {errors?.firstName && (
                    <Typography variant="body2" component="li" css={getAddressErrorStyles}>
                        {errors?.firstName}
                    </Typography>
                )}
                {errors?.lastName && (
                    <Typography variant="body2" component="li" css={getAddressErrorStyles}>
                        {errors?.lastName}
                    </Typography>
                )}
                {textContent.company && (
                    <Typography
                        variant="body2"
                        component="li"
                        css={errors?.company && getAddressErrorStyles}
                        aria-label={ariaLabelFor('company')}
                    >
                        {textContent.company}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    component="li"
                    css={errors?.street1 && getAddressErrorStyles}
                    aria-label={ariaLabelFor('street1')}
                >
                    {textContent.street1}
                </Typography>
                {textContent.street2 && (
                    <Typography
                        variant="body2"
                        component="li"
                        css={errors?.street2 && getAddressErrorStyles}
                        aria-label={ariaLabelFor('street2')}
                    >
                        {textContent.street2}
                    </Typography>
                )}
                {textContent.city && (
                    <Typography
                        variant="body2"
                        component="li"
                        css={errors?.city && getAddressErrorStyles}
                        aria-label={ariaLabelFor('city')}
                    >
                        {textContent.city}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    component="li"
                    css={errors?.alpha2Code && getAddressErrorStyles}
                    aria-label={ariaLabelFor('alpha2Code')}
                >
                    {textContent.alpha2Code}
                </Typography>
                <Typography
                    variant="body2"
                    component="li"
                    css={errors?.zipCode && getAddressErrorStyles}
                    aria-label={ariaLabelFor('zipCode')}
                >
                    {textContent.zipCode}
                </Typography>
                <Typography
                    variant="body2"
                    component="li"
                    css={errors?.email && getAddressErrorStyles}
                    aria-label={ariaLabelFor('email')}
                >
                    {textContent.email}
                </Typography>
                <Typography
                    variant="body2"
                    component="li"
                    css={errors?.phone && getAddressErrorStyles}
                    aria-label={ariaLabelFor('phone')}
                >
                    {textContent.phone}
                </Typography>
            </ul>
            <Button
                variant={ButtonVariant.TEXT}
                onClick={onClickEdit}
                aria-label={t('address.actions.edit-aria-label')}
            >
                {t('address.actions.edit-label')}
            </Button>
            <Dialog
                isOpen={isFormOpen}
                shouldCloseOnOverlayClick={false}
                className="dialog"
                onRequestClose={onCancelDialog}
            >
                <DialogHeader
                    showClose={true}
                    title={t('address.dialog.title')}
                    closeAriaLabel={'address.dialog.close'}
                    className="dialog__header"
                    onClose={onCloseDialog}
                />
                <DialogContent>
                    <AddressForm
                        initialValues={initialValues as AddressModel}
                        origin={originType}
                        title={t('address-form.section.title', { title })}
                        onSave={onSaveHandler}
                        onCancel={onCancelDialog}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Address;
